import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Badge,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search,
  Favorite,
  FavoriteBorder,
  DirectionsCar,
  Speed,
  LocalGasStation,
  CalendarToday,
  ExpandMore,
  FilterList,
  ViewModule,
  ViewList,
  Star
} from '@mui/icons-material';

const CarAuctionInterface = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [selectedTab, setSelectedTab] = useState(0);
  const [favorites, setFavorites] = useState(new Set());
  const [filters, setFilters] = useState({
    searchTerm: '',
    make: '',
    model: '',
    yearFrom: '',
    yearTo: '',
    priceFrom: '',
    priceTo: '',
    transmission: '',
    fuelType: ''
  });

  // Sample car data
  const cars = [
    {
      id: 1,
      name: 'Mercedes-Benz E-Class',
      year: 2020,
      mileage: '45,000 km',
      price: 'Gh 35,000',
      transmission: 'Automatic',
      fuel: 'Gasoline',
      image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=500',
      rating: 4.5,
      location: 'Seoul, Korea',
      auctionEnd: '2 days Ago'
    },
    {
      id: 2,
      name: 'BMW 5 Series',
      year: 2019,
      mileage: '52,000 km',
      price: 'Gh 32,000',
      transmission: 'Automatic',
      fuel: 'Diesel',
      image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=500',
      rating: 4.3,
      location: 'Busan, Korea',
      auctionEnd: '1 day Ago'
    },
    {
      id: 3,
      name: 'Audi A6',
      year: 2021,
      mileage: '28,000 km',
      price: 'Gh 42,000',
      transmission: 'Automatic',
      fuel: 'Gasoline',
      image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=500',
      rating: 4.7,
      location: 'Incheon, Korea',
      auctionEnd: '3 days Ago'
    },
    {
      id: 4,
      name: 'Toyota Camry',
      year: 2020,
      mileage: '38,000 km',
      price: ' Gh 28,000',
      transmission: 'Automatic',
      fuel: 'Hybrid',
      image: 'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=500',
      rating: 4.4,
      location: 'Seoul, Korea',
      auctionEnd: '5 days Ago'
    },
    {
      id: 5,
      name: 'Hyundai Genesis',
      year: 2021,
      mileage: '22,000 km',
      price: 'Gh 38,000',
      transmission: 'Automatic',
      fuel: 'Gasoline',
      image: 'https://images.unsplash.com/photo-1617531653332-bd46c24f2068?w=500',
      rating: 4.6,
      location: 'Daegu, Korea',
      auctionEnd: '4 days Ago'
    },
    {
      id: 6,
      name: 'Kia Stinger',
      year: 2020,
      mileage: '41,000 km',
      price: 'Gh 31,000',
      transmission: 'Automatic',
      fuel: 'Gasoline',
      image: 'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=500',
      rating: 4.2,
      location: 'Gwangju, Korea',
      auctionEnd: '2 days Ago'
    }
  ];

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const CarCard = ({ car }) => (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: 6
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="200"
          image={car.image}
          alt={car.name}
          sx={{ objectFit: 'cover' }}
        />
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'white',
            '&:hover': { bgcolor: 'grey.100' }
          }}
          onClick={() => toggleFavorite(car.id)}
        >
          {favorites.has(car.id) ? (
            <Favorite color="error" />
          ) : (
            <FavoriteBorder />
          )}
        </IconButton>
        <Chip
          label={car.auctionEnd}
          size="small"
          color="error"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            fontWeight: 600
          }}
        />
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight={600} noWrap>
          {car.name}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {car.year}
          </Typography>
          <Speed sx={{ fontSize: 18, color: 'text.secondary', ml: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {car.mileage}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DirectionsCar sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {car.transmission}
          </Typography>
          <LocalGasStation sx={{ fontSize: 18, color: 'text.secondary', ml: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {car.fuel}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
          <Star sx={{ fontSize: 18, color: 'warning.main' }} />
          <Typography variant="body2" fontWeight={600}>
            {car.rating}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {car.location}
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" color="primary" fontWeight={700}>
            {car.price}
          </Typography>
          <Button variant="contained" size="small" sx={{ textTransform: 'none' }}>
            Bid Now
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" fontWeight={700} color="primary">
              Johns Vehicle Auction
            </Typography>
            <Badge badgeContent={favorites.size} color="error">
              <IconButton>
                <Favorite />
              </IconButton>
            </Badge>
          </Box>

          <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
            <Tab label="All Vehicles" />
            <Tab label="Sedans" />
            <Tab label="SUVs" />
            <Tab label="Sports" />
            <Tab label="Electric" />
          </Tabs>
        </Paper>

        <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
          {/* Filters Sidebar */}
          <Grid item xs={12} md={3} size={3}>
            <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FilterList sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Filters
                </Typography>
              </Box>

              <TextField
                fullWidth
                placeholder="Search vehicles..."
                value={filters.searchTerm}
                onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>Price Range</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Min"
                        placeholder="$0"
                        size="small"
                        value={filters.priceFrom}
                        onChange={(e) => setFilters({...filters, priceFrom: e.target.value})}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Max"
                        placeholder="$100k"
                        size="small"
                        value={filters.priceTo}
                        onChange={(e) => setFilters({...filters, priceTo: e.target.value})}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>Year</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="From"
                        placeholder="2015"
                        size="small"
                        value={filters.yearFrom}
                        onChange={(e) => setFilters({...filters, yearFrom: e.target.value})}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="To"
                        placeholder="2024"
                        size="small"
                        value={filters.yearTo}
                        onChange={(e) => setFilters({...filters, yearTo: e.target.value})}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>Transmission</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Automatic"
                  />
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Manual"
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>Fuel Type</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControlLabel control={<Checkbox />} label="Gasoline" />
                  <FormControlLabel control={<Checkbox />} label="Diesel" />
                  <FormControlLabel control={<Checkbox />} label="Hybrid" />
                  <FormControlLabel control={<Checkbox />} label="Electric" />
                </AccordionDetails>
              </Accordion>

              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 2, textTransform: 'none' }}
              >
                Apply Filters
              </Button>
              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Reset
              </Button>
            </Paper>
          </Grid>

          {/* Vehicle Grid */}
          <Grid item xs={12} md={9} size={9}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">
                  <strong>{cars.length}</strong> vehicles found
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select label="Sort By" defaultValue="newest">
                      <MenuItem value="newest">Newest First</MenuItem>
                      <MenuItem value="price-low">Price: Low to High</MenuItem>
                      <MenuItem value="price-high">Price: High to Low</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                      <MenuItem value="mileage">Mileage</MenuItem>
                    </Select>
                  </FormControl>

                  <Box>
                    <IconButton
                      onClick={() => setViewMode('grid')}
                      color={viewMode === 'grid' ? 'primary' : 'default'}
                    >
                      <ViewModule />
                    </IconButton>
                    <IconButton
                      onClick={() => setViewMode('list')}
                      color={viewMode === 'list' ? 'primary' : 'default'}
                    >
                      <ViewList />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
              {cars.map(car => (
                <Grid item xs={12} sm={6} lg={4} key={car.id} size={4}>
                  <CarCard car={car} />
                </Grid>
              ))}
            </Grid>

            {/* Pagination would go here */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button variant="outlined" sx={{ mx: 1 }}>Previous</Button>
              <Button variant="contained" sx={{ mx: 1 }}>1</Button>
              <Button variant="outlined" sx={{ mx: 1 }}>2</Button>
              <Button variant="outlined" sx={{ mx: 1 }}>3</Button>
              <Button variant="outlined" sx={{ mx: 1 }}>Next</Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CarAuctionInterface;